/**
 * Межосевое «До след.» между соседними контурами этажного коллектора (верх/низ).
 * Базовое значение на тип фитинга; между i и i+1: round5((base[i]+base[i+1])/2).
 */

import fittingsData from '../fittingsData';
import { getTransitionPipeDiameter } from './endConnectionVelocitySizing';
import { roundContourSpacingToStep5 } from './distributionCollectorOffsets';

const NIPPLE_MUFTA_SUFFIX_TO_NOMINAL_PIPE_MM = {
  '1.4': 13.7,
  '3.8': 17.2,
  '1.2': 21.3,
  '3.4': 26.9,
  '1': 33.7,
  '1_1.4': 42.4,
  '1_1.2': 48.3,
  '2': 60.3,
  '2_1/2': 76.1,
  '3': 88.9,
  '4': 114.3,
};

function nominalPipeMmFromNippleOrMuftaId(fittingId) {
  const m = fittingId.match(/^Nippel_(.+)$/) || fittingId.match(/^Mufta_(.+)$/);
  if (!m) return null;
  const mm = NIPPLE_MUFTA_SUFFIX_TO_NOMINAL_PIPE_MM[m[1]];
  return mm != null ? mm : null;
}

function refPipeMmForFloorFitting(fittingId, fittingCategory) {
  if (fittingCategory === 'Ниппели' || fittingCategory === 'Муфты') {
    const fromSuffix = nominalPipeMmFromNippleOrMuftaId(fittingId);
    if (fromSuffix != null) return fromSuffix;
    const d = Number(fittingsData[fittingId]?.diameter);
    return Number.isFinite(d) ? d : null;
  }
  if (fittingCategory === 'Накидные гайки') {
    const m = fittingId.match(/^Nak_Gaika_(.+)$/);
    if (!m) return null;
    const threadNominal = NIPPLE_MUFTA_SUFFIX_TO_NOMINAL_PIPE_MM[m[1]];
    if (threadNominal == null) return null;
    return getTransitionPipeDiameter('nut', threadNominal);
  }
  if (fittingCategory === 'Шайбы с наружной резьбой') {
    const key = Number(fittingsData[fittingId]?.diameter);
    if (!Number.isFinite(key)) return null;
    return getTransitionPipeDiameter('washer', key);
  }
  return null;
}

function nippleNominalToFloorSpacingBaseMm(refMm) {
  if (!Number.isFinite(refMm)) return 125;
  if (refMm <= 26.9) return 100;
  if (refMm <= 33.7) return 125;
  if (refMm <= 42.4) return 125;
  if (refMm <= 48.3) return 200;
  if (refMm <= 60.3) return 250;
  if (refMm <= 76.1) return 300;
  if (refMm <= 88.9) return 300;
  if (refMm <= 114.3) return 300;
  return 300;
}

/** Ду фланца → базовое межосевое (мм) для этажного коллектора */
const FLOOR_FLANGE_DN_TO_SPACING_MM = {
  25: 125, // труба 33,7 мм — как ниппель 1″; без ключа попадали в fallback 300
  32: 150,
  40: 200,
  50: 300,
  65: 300,
  80: 300,
  100: 300,
  125: 300,
  150: 300,
  200: 400,
  250: 450,
  300: 500,
  350: 550,
};

/**
 * Базовое межосевое для одного контура (как если бы сосед был таким же).
 * @param {object} circuit — элемент circuits / bottomCircuits редактора
 */
export function getFloorCircuitSpacingBaseMm(circuit) {
  if (!circuit) return 125;
  if (circuit.type === 'pipe') {
    const d = Number(circuit.diameter);
    return Number.isFinite(d) ? nippleNominalToFloorSpacingBaseMm(d) : 125;
  }
  const { fittingId, fittingCategory } = circuit;
  if (!fittingId || !fittingCategory) return 125;

  if (fittingCategory === 'Фланцы') {
    const m = fittingId.match(/^Flanec_(\d+)_/);
    if (m) {
      const dn = Number(m[1]);
      if (FLOOR_FLANGE_DN_TO_SPACING_MM[dn] != null) {
        return FLOOR_FLANGE_DN_TO_SPACING_MM[dn];
      }
    }
    return 300;
  }

  if (
    fittingCategory === 'Ниппели' ||
    fittingCategory === 'Муфты' ||
    fittingCategory === 'Накидные гайки' ||
    fittingCategory === 'Шайбы с наружной резьбой'
  ) {
    const refMm = refPipeMmForFloorFitting(fittingId, fittingCategory);
    return nippleNominalToFloorSpacingBaseMm(refMm);
  }

  return 125;
}

/**
 * Массив spacingToNext по индексу контура (последний — null).
 */
export function computeFloorCircuitsSpacingToNext(circuits) {
  const n = circuits?.length ?? 0;
  if (n <= 1) {
    return Array.from({ length: n }, () => null);
  }
  return circuits.map((_, i) => {
    if (i >= n - 1) return null;
    const a = getFloorCircuitSpacingBaseMm(circuits[i]);
    const b = getFloorCircuitSpacingBaseMm(circuits[i + 1]);
    return roundContourSpacingToStep5((a + b) / 2);
  });
}

/** Проставить вычисленные spacingToNext каждому контуру. */
export function mergeFloorCircuitAutoSpacingToNext(circuits) {
  if (!circuits || circuits.length === 0) return circuits;
  const spacings = computeFloorCircuitsSpacingToNext(circuits);
  return circuits.map((c, i) => ({ ...c, spacingToNext: spacings[i] }));
}

// ─── Отступы первого/последнего контура: расчёт по характеристикам (этажный коллектор) ───

const FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM = 60;

/** Ду фланца → отступ от края до центра первого/последнего подключения (мм). */
const FLOOR_CHAR_FLANGE_DN_EDGE_MM = {
  25: 100, // как Ду32 в этой таблице
  32: 100,
  40: 100,
  50: 100,
  65: 100,
  80: 100,
  100: 120,
  125: 130,
  150: 150,
  200: 180,
  250: 210,
  300: 240,
  350: 270,
};

function nippleNominalToCharacteristicsEdgeMm(refMm) {
  if (!Number.isFinite(refMm)) return FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM;
  if (refMm <= 26.9) return 30;
  if (refMm <= 33.7) return 50;
  if (refMm <= 42.4) return 50;
  if (refMm <= 48.3) return 55;
  if (refMm <= 60.3) return 60;
  if (refMm <= 76.1) return 70;
  if (refMm <= 88.9) return 75;
  if (refMm <= 114.3) return 90;
  return 90;
}

/**
 * Отступ края до оси подключения по фитингу (расчёт по характеристикам).
 */
export function getFloorCharacteristicsEdgeOffsetMm(fittingId, fittingCategory) {
  if (!fittingId || !fittingCategory) return FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM;

  if (fittingCategory === 'Фланцы') {
    const m = fittingId.match(/^Flanec_(\d+)_/);
    if (m) {
      const dn = Number(m[1]);
      if (FLOOR_CHAR_FLANGE_DN_EDGE_MM[dn] != null) return FLOOR_CHAR_FLANGE_DN_EDGE_MM[dn];
    }
    return 100;
  }

  if (
    fittingCategory === 'Ниппели' ||
    fittingCategory === 'Муфты' ||
    fittingCategory === 'Накидные гайки' ||
    fittingCategory === 'Шайбы с наружной резьбой'
  ) {
    return nippleNominalToCharacteristicsEdgeMm(refPipeMmForFloorFitting(fittingId, fittingCategory));
  }

  return FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM;
}

function edgeForFloorRowEnds(circuits) {
  const n = circuits?.length ?? 0;
  if (n === 0) {
    return { first: FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM, last: FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM };
  }
  const idAt = (c) => c.fitting ?? c.fittingId;
  return {
    first: getFloorCharacteristicsEdgeOffsetMm(idAt(circuits[0]), circuits[0].fittingCategory),
    last: getFloorCharacteristicsEdgeOffsetMm(idAt(circuits[n - 1]), circuits[n - 1].fittingCategory),
  };
}

/**
 * Параметры firstCircuitOffset / lastCircuitOffset (верх) и bottom* (низ) для этажного коллектора.
 * Верх: по первому/последнему контуру верхнего ряда; если верх пуст — по нижнему ряду.
 * Низ: по нижнему ряду; если низ пуст — те же величины, что у верха.
 */
export function getFloorCharacteristicsFirstLastOffsetsFromRows(topCircuits, bottomCircuits) {
  const top = edgeForFloorRowEnds(topCircuits);
  const bot = edgeForFloorRowEnds(bottomCircuits);
  const topN = topCircuits?.length ?? 0;
  const botN = bottomCircuits?.length ?? 0;

  return {
    firstCircuitOffset:
      topN > 0 ? top.first : botN > 0 ? bot.first : FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM,
    lastCircuitOffset:
      topN > 0 ? top.last : botN > 0 ? bot.last : FLOOR_CHARACTERISTICS_DEFAULT_EDGE_MM,
    bottomFirstCircuitOffset: botN > 0 ? bot.first : top.first,
    bottomLastCircuitOffset: botN > 0 ? bot.last : top.last,
  };
}
